import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/auth/verify-email';

export interface VerifyAccountInput {
    token: string;
    email: string;
}

export interface VerifyAccountSuccessResponse {
    success: boolean;
    message: string;
    data: unknown;
}

/**
 * Requests endpoint to verify user account.
 */
export const useVerifyAccount = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();

    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async (props: VerifyAccountInput) => {
        setLoading(true);
        setSuccess(false);
        setErrorMessage(null);

        try {
            const queryParams = new URLSearchParams({
                token: props.token,
                email: props.email,
            });

            const response = await fetch(`${API_URL}${ENDPOINT_URL}?${queryParams.toString()}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch {
                    error.message = '';
                }

                error.statusCode = response.status;

                throw error;
            }

            setSuccess(true);
        } catch (error: unknown) {
            handleCommonErrors(error as ApiError);
            if (error instanceof Error && error.message) {
                setErrorMessage(error.message);
            }
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, success, errorMessage, lastResponseTime };
};
